import { isCustomCodeDashlet } from 'components/DashboardGrid/hooks/customCodeBlock';
import type { AiBlockPanelContext } from './connectOptions';
import {
  getDashletConversationId,
  isAiBlockDashlet,
  type DashletWithAiBlock,
} from './aiBlockMarker';

export const AI_BLOCK_TYPE = 'ai_block' as const;

export type AiBlockSessionState =
  | { status: 'closed' }
  | {
      status: 'open';
      mode: 'create' | 'edit';
      sessionKey: string;
      context: AiBlockPanelContext;
      dashletId?: string;
      conversationId?: string;
    };

export type AiBlockSessionAction =
  | { type: 'open_create'; sessionKey: string; context: AiBlockPanelContext }
  | {
      type: 'open_edit';
      sessionKey: string;
      context: AiBlockPanelContext;
      dashletId: string;
      conversationId: string;
    }
  | {
      type: 'block_pushed';
      sessionKey: string;
      dashletId: string;
      conversationId: string;
    }
  | { type: 'close' };

export const CLOSED_SESSION: AiBlockSessionState = { status: 'closed' };

export const aiBlockSessionReducer = (
  state: AiBlockSessionState,
  action: AiBlockSessionAction
): AiBlockSessionState => {
  switch (action.type) {
    case 'open_create':
      return {
        status: 'open',
        mode: 'create',
        sessionKey: action.sessionKey,
        context: action.context,
      };
    case 'open_edit':
      if (
        state.status === 'open' &&
        state.dashletId === action.dashletId &&
        state.context.dashboardId === action.context.dashboardId
      ) {
        return state.mode === 'edit' ? state : { ...state, mode: 'edit' };
      }
      return {
        status: 'open',
        mode: 'edit',
        sessionKey: action.sessionKey,
        context: action.context,
        dashletId: action.dashletId,
        conversationId: action.conversationId,
      };
    case 'block_pushed':
      if (
        state.status !== 'open' ||
        state.sessionKey !== action.sessionKey ||
        (state.dashletId === action.dashletId &&
          state.conversationId === action.conversationId)
      ) {
        return state;
      }
      return {
        ...state,
        dashletId: action.dashletId,
        conversationId: action.conversationId,
      };
    case 'close':
      return CLOSED_SESSION;
    default:
      return state;
  }
};

export const reconcileSessionWithPage = (
  state: AiBlockSessionState,
  dashboardId: string | null | undefined
): Extract<AiBlockSessionAction, { type: 'close' }> | null =>
  state.status === 'open' &&
  Boolean(dashboardId) &&
  state.context.dashboardId !== dashboardId
    ? { type: 'close' }
    : null;

export const resolveVisibleSession = (
  state: AiBlockSessionState,
  dashboardId: string | null | undefined
): AiBlockSessionState =>
  state.status === 'open' &&
  (!dashboardId || state.context.dashboardId === dashboardId)
    ? state
    : CLOSED_SESSION;

export const getFocusedDashletId = (
  state: AiBlockSessionState
): string | undefined =>
  state.status === 'open' && state.mode === 'edit'
    ? state.dashletId
    : undefined;

export const getSessionDashletId = (
  state: AiBlockSessionState
): string | undefined =>
  state.status === 'open' ? state.dashletId : undefined;

export const isSessionAiBlock = (
  dashlet: (DashletWithAiBlock & { id?: unknown }) | null | undefined,
  sessionDashletId?: string | null
): boolean =>
  isAiBlockDashlet(dashlet) ||
  Boolean(sessionDashletId && dashlet?.id === sessionDashletId);

export const shouldRenderPlaceholder = (
  focusedDashletId: string | null | undefined,
  dashletId: string
): boolean => Boolean(focusedDashletId) && dashletId !== focusedDashletId;

export type AiBlockEditAction = 'ai_panel' | 'json_editor' | 'other';

export const resolveEditAction = (
  dashlet:
    | (DashletWithAiBlock & { id?: unknown; config?: any })
    | null
    | undefined,
  sessionDashletId?: string | null
): AiBlockEditAction => {
  if (sessionDashletId && dashlet?.id === sessionDashletId) {
    return 'ai_panel';
  }
  if (isAiBlockDashlet(dashlet)) return 'ai_panel';
  if (isCustomCodeDashlet(dashlet)) {
    return 'json_editor';
  }
  return 'other';
};

export const buildOpenEditAction = (
  dashlet: (DashletWithAiBlock & { id: string }) | null | undefined,
  context: AiBlockPanelContext,
  sessionKey: string,
  session?: { dashletId?: string; conversationId?: string } | null
): Extract<AiBlockSessionAction, { type: 'open_edit' }> | null => {
  if (
    !dashlet ||
    resolveEditAction(dashlet, session?.dashletId) !== 'ai_panel'
  ) {
    return null;
  }
  const conversationId =
    getDashletConversationId(dashlet) ??
    (session?.dashletId === dashlet.id ? session.conversationId : undefined);
  if (!conversationId) return null;
  return {
    type: 'open_edit',
    sessionKey,
    context,
    dashletId: dashlet.id,
    conversationId,
  };
};

export const buildAiPanelContext = (
  targetType: AiBlockPanelContext['targetType'],
  dashboardId: string,
  objectId?: string | null
): AiBlockPanelContext =>
  objectId
    ? { targetType, dashboardId, objectId }
    : { targetType, dashboardId };

export interface AiPanelProps {
  key: string;
  context: AiBlockPanelContext;
  dashletId?: string;
  conversationId?: string;
}

export const buildAiPanelProps = (
  state: AiBlockSessionState
): AiPanelProps | null => {
  if (state.status !== 'open') return null;
  return {
    key: state.sessionKey,
    context: state.context,
    ...(state.dashletId ? { dashletId: state.dashletId } : {}),
    ...(state.dashletId && state.conversationId
      ? { conversationId: state.conversationId }
      : {}),
  };
};

type DashletLayoutLike = {
  i?: string;
  h?: number;
  w?: number;
  x?: number;
  y?: number;
};

export interface DashletUpdateRequest {
  dashletId: string;
  dashboardId: string;
  payload: {
    config?: unknown;
    name?: string;
    layout?: DashletLayoutLike;
    [key: string]: unknown;
  };
  layoutUpdate?: boolean;
  [key: string]: unknown;
}

export const toAiBlockSafeUpdate = <T extends DashletUpdateRequest>(
  request: T,
  existingDashlet: (DashletWithAiBlock & { id?: unknown }) | null | undefined,
  sessionDashletId?: string | null
): T | (Omit<T, 'payload'> & { payload: { layout?: DashletLayoutLike } }) => {
  if (!isSessionAiBlock(existingDashlet, sessionDashletId)) return request;
  return {
    ...request,
    payload: { layout: request.payload.layout },
    layoutUpdate: true,
  };
};
